import { IFilters } from "components/Filters/Filters.def";
import { isString } from "lodash-es";
import { Fragment, createElement } from "react";
import { apiBaseUrl, propertyTypeObject, propTypeCodeObject } from "utils/constants";
import { SCHOOL_KEYS } from "components/AiSummaryDrawer/AiSummaryDrawer.def";
import { KeysOfAnyType, TypedEntries } from "types/utility";
import type { ISoldPriceDisplay } from "types/services";


export const joinWithSeparator = (
  separator: string,
  ...values: Array<join.Value>
): string => {
  return values.filter((x) => !!x && !!x?.toString()?.trim()).join(separator);
};

export const joinWithSpace = (...values: Array<join.Value>): string => {
  return joinWithSeparator(" ", ...values);
};

export const joinWithDot = (...values: Array<join.Value>): string => {
  return joinWithSeparator(" · ", ...values);
};

export function join(...values: Array<join.Value>): string {
  return joinWithSeparator("", ...values);
}

export namespace join {
  export type Value = string | number | false | undefined;
}

/**
 * Extract the raw numeric base HAR ID from a composite harid.
 *
 * Composite format mirrors the backend: `{harid}-{id}-{status}`
 * (e.g. "12345-67890-A" or "12345-0-none"). URLs and the website expect the
 * bare numeric harid, NOT the composite, so this must be called before
 * building any /home-gallery, /listing, etc. URL from a harid.
 *
 * Handles:
 *  - Full composite "12345-67890-A" -> "12345"
 *  - "12345-0-none"                 -> "12345"
 *  - Legacy bare harid "12345"      -> "12345"
 *  - Missing / empty                -> undefined
 */
export const parseCompositeHarid = (
  composite?: string | number | null
): string | undefined => {
  if (composite === null || composite === undefined) return undefined;
  const str = String(composite).trim();
  if (!str) return undefined;

  // Split from the right into at most 3 parts: harid, id, status.
  const lastDash = str.lastIndexOf("-");
  if (lastDash !== -1) {
    const secondLastDash = str.lastIndexOf("-", lastDash - 1);
    if (secondLastDash !== -1) {
      const haridPart = str.slice(0, secondLastDash);
      const idPart = str.slice(secondLastDash + 1, lastDash);
      // A valid composite has digit-only harid and id parts.
      if (/^\d+$/.test(haridPart) && /^\d+$/.test(idPart)) {
        return haridPart;
      }
    }
  }

  // Fallback: legacy bare numeric harid.
  if (/^\d+$/.test(str)) return str;

  // Unknown format — return as-is rather than dropping it.
  return str;
};

const COMPOSITE_SENTINEL_ID = "0";

/**
 * The real, disambiguating listing id embedded in a composite harid
 * ("{harid}-{id}-{status}"), or undefined for a bare harid or the "0" sentinel
 * (produced by format_composite_harid when no instance is known, e.g.
 * "{harid}-0-none"). Used to tell two genuinely different listing instances
 * apart from a base-only harid.
 */
const compositeInstanceId = (value: string | number): string | undefined => {
  const str = String(value).trim();
  const lastDash = str.lastIndexOf("-");
  if (lastDash === -1) return undefined;
  const secondLastDash = str.lastIndexOf("-", lastDash - 1);
  if (secondLastDash === -1) return undefined;
  const haridPart = str.slice(0, secondLastDash);
  const idPart = str.slice(secondLastDash + 1, lastDash);
  if (!/^\d+$/.test(haridPart) || !/^\d+$/.test(idPart)) return undefined;
  return idPart === COMPOSITE_SENTINEL_ID ? undefined : idPart;
};

/**
 * True when `target` and an artifact's `harid` refer to the same property.
 *
 * Either side may be a composite ("{harid}-{id}-{status}"), a legacy bare
 * harid ("3504671"), or a numeric harid (3504671). Strategy:
 *  1. Exact string match first — preserves id/status disambiguation when the
 *     model and the artifact both carry the same composite.
 *  2. If BOTH sides carry a real (non-sentinel) listing id and those ids
 *     differ, they are distinct instances of the same base property (e.g.
 *     for-sale vs for-rent) — do NOT collapse them.
 *  3. Otherwise at least one side is a bare/base harid: fall back to comparing
 *     the base harids extracted via parseCompositeHarid, so a composite target
 *     still matches an artifact that only carries the base.
 */
export const haridMatches = (
  artifactHarid?: string | number | null,
  target?: string | number | null
): boolean => {
  if (artifactHarid == null || target == null) return false;
  if (String(artifactHarid) === String(target)) return true;
  const aId = compositeInstanceId(artifactHarid);
  const bId = compositeInstanceId(target);
  if (aId !== undefined && bId !== undefined && aId !== bId) return false;
  const a = parseCompositeHarid(artifactHarid);
  const b = parseCompositeHarid(target);
  return a !== undefined && a === b;
};

/** Find the first artifact whose harid matches `target` (composite-aware). */
export const findArtifactByHarid = <T extends { harid?: string | number }>(
  artifacts: T[],
  target?: string | number | null
): T | undefined => artifacts.find((x) => haridMatches(x.harid, target));

export const joinJSXWithSeparator = (
  separator: string | JSX.Element | ((index: number) => JSX.Element),
  ...values: Array<JSX.Element | string | false | undefined>
): JSX.Element => {
  const validItems = values.filter((x) => x && (isString(x) ? x.trim() : true));
  if (!validItems.length) {
    return <></>;
  }

  const jsxSeparator = isString(separator) ? <>{separator}</> : separator;
  const nodes = [validItems.shift()!];
  let index = 0;
  while (validItems.length) {
    nodes.push("call" in jsxSeparator ? jsxSeparator(index++) : jsxSeparator);
    nodes.push(validItems.shift()!);
  }

  return createElement(Fragment, {}, nodes);
};

export const joinJSXWithSpace = (
  ...values: Array<JSX.Element | string | false | undefined>
): JSX.Element => {
  return joinJSXWithSeparator(" ", ...values);
};

export const joinJSX = (
  ...values: Array<JSX.Element | string | false | undefined>
): JSX.Element => {
  return joinJSXWithSeparator("", ...values);
};

export function twoArraysToObject(arr1: Array<string>, arr2: Array<string>) {
  return arr1.reduce((obj: { [key: string]: string }, item, index) => {
    obj[item] = arr2[index];
    return obj;
  }, {});
}

export function isEmpty(value: string | undefined) {
  if (typeof value != "undefined" && value && value != "") {
    return false;
  }
  return true;
}


export const getLongValue = (
  lookup: ReadonlyArray<{ value: string; label: string }>,
  value: string | string[] | undefined,
  defaultValue?: string
) => {
  if (!value) {
    return defaultValue;
  }
  return lookup.find((x) => x.value === value)?.label ?? defaultValue;
};

/**
 * Mirror of `getLongValue`: resolve a lookup entry's label back to its value.
 * Needed by the ingress paths that receive a display name where the UI filter
 * is contracted to hold the underlying id (e.g. the legacy `county` key, which
 * carries "Harris" rather than the FIPS code the <select> matches on).
 */
export const getShortValue = (
  lookup: ReadonlyArray<{ value: string; label: string }>,
  label: string | undefined,
  defaultValue?: string
) => {
  if (!label) {
    return defaultValue;
  }
  return lookup.find((x) => x.label === label)?.value ?? defaultValue;
};

/**
 * Convert an array of { value, label } items into
 * an object of the form { [value]: label }.
 *
 * @param {Array<{value: string, label: string}>} list
 * @returns {Record<string, string>}
 */
export function makeObjectFromValueLabelOptons<
  T extends { value: string; label: string },
>(list: T[]): Record<string, string> {
  return list.reduce<Record<string, string>>((acc, { value, label }) => {
    acc[value] = label;
    return acc;
  }, {});
}

export const formatAmountIntoUSD = (number: string | number = 0): string => {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
  }).format(Number(number));
};

/**
 * Format a raw price into USD, returning "" when there is no usable number.
 *
 * Unlike formatAmountIntoUSD, which coerces null/undefined to 0 and renders
 * "$0" (or "$NaN" for an unparseable string), this returns "" so a caller can
 * render nothing rather than a price that does not exist. Zero counts as
 * missing: no HAR listing is genuinely worth $0, and it is the default for
 * PropertyDetailBasic.price, so treating it as a real price would show a
 * fabricated one.
 */
export const formatPriceOrEmpty = (
  price: string | number | null | undefined
): string => {
  if (price === null || price === undefined || price === "") return "";
  const numeric =
    typeof price === "string"
      ? parseFloat(price.replace(/[^0-9.-]/g, ""))
      : Number(price);
  if (!Number.isFinite(numeric) || numeric === 0) return "";
  return "$" + numeric.toLocaleString("en-US");
};

/**
 * Resolve the price to display for a listing.
 *
 * Sold listings carry a `sold_price_display` built by the API, which decides
 * what the current user is allowed to see - the exact sold price, or only a
 * range. Everything else falls back to the list price. Keeping the choice here
 * means no component re-implements the permission rule.
 *
 * The two empty cases are deliberately different. A sold listing with nothing
 * to show reads "Price not disclosed", because the API told us it is sold and
 * the figure was withheld or is absent. A listing with no `sold_price_display`
 * and no usable price renders "" instead: it is an active listing missing
 * data, and saying its price is "not disclosed" would claim something we do
 * not know.
 */
export const resolveListingPrice = (
  soldPriceDisplay: ISoldPriceDisplay | null | undefined,
  listPrice: string | number | null | undefined
): { value: string; sqft: string | null } => {
  if (soldPriceDisplay) {
    return {
      value: soldPriceDisplay.value || "Price not disclosed",
      // Per-sqft is only shown beside an exact price. Next to a range it would
      // hand back the withheld figure (per-sqft x building sqft), so it is
      // dropped here even if the API supplied one - the same rule har.com
      // applies server side. This is the last point before it reaches a screen.
      sqft: soldPriceDisplay.disclosed ? soldPriceDisplay.sqft || null : null,
    };
  }
  return { value: formatPriceOrEmpty(listPrice), sqft: null };
};

export const currencyFormatter = (
  number: string | number = 0,
  withSymbol = false
) => {
  return new Intl.NumberFormat("en-US", {
    style: withSymbol ? "currency" : "decimal",
    currency: "USD",
    notation: "compact",
    compactDisplay: "short",
    // maximumFractionDigits: 0, // round to whole units
  }).format(Number(number));
};

export const numberFormatter = (number: number = 0) =>
  Intl.NumberFormat("en-US").format(Number(number));

export const shareConversation = (sessionId: string, harId?: string) => {
  if (!sessionId) return;

  const path = harId
    ? `listing/${encodeURIComponent(harId)}/${encodeURIComponent(
      sessionId
    )}/share`
    : `${encodeURIComponent(sessionId)}/share`;

  // Open the backend share endpoint; it will redirect to HAR.
  window.open(`${apiBaseUrl}/${path}`, "_blank", "noopener,noreferrer");
};

export const splitIntoArray = <T extends string,>(value: T | T[] | undefined): T[] => {
  if (!value) return [];
  if (Array.isArray(value)) return value;
  if (typeof value === "string") {
    return value.split(",").map((item) => item.trim()).filter(Boolean) as T[];
  }
  return [];
}

export function getFormattedTimestamp(timestamp?: string | Date): string {
  let date: Date | null = null;

  if (timestamp instanceof Date) {
    date = timestamp;
  } else if (typeof timestamp === "string") {
    date = new Date(timestamp);
  }

  if (!date) {
    return new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  }

  const now = new Date();
  const sameDay =
    date.getDate() === now.getDate() &&
    date.getMonth() === now.getMonth() &&
    date.getFullYear() === now.getFullYear();

  const yesterday = new Date(now.getTime() - 24 * 60 * 60 * 1000);
  const isYesterday =
    date.getDate() === yesterday.getDate() &&
    date.getMonth() === yesterday.getMonth() &&
    date.getFullYear() === yesterday.getFullYear();

  const timeStr = date.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });

  if (sameDay) {
    // Same day → only time
    return timeStr;
  } else if (isYesterday) {
    // Yesterday
    return `Yesterday at ${timeStr}`;
  } else if (date.getFullYear() === now.getFullYear()) {
    // Same year → Month day at time
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
    }) + ` at ${timeStr}`;
  } else {
    // Past years → Month day, year at time
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    }) + ` at ${timeStr}`;
  }
}


export const reversedPropertyTypeObject: Record<string, string> = Object.fromEntries(
  Object.entries(propertyTypeObject).map(([key, value]) => [value, key])
);

// Normalize property-type values to canonical NAMES. `property_class_id` is
// overloaded in both its element type and its container: it carries numeric ids
// when derived from native HAR filters and names when derived from UI filters,
// and the tool artifact sends the ids comma-separated ("1,2") rather than as an
// array — a bare string has a truthy `.length`, so reading it as an array threw
// "values.map is not a function" and took the whole filter conversion (and with
// it the filters modal) down with it. Consumers that need names (the modal's
// `PropertyType`, and the tool input's `property_type`, whose backend maps
// names -> ids) must normalize first. Idempotent: names pass through unchanged
// since they are not keys of `reversedPropertyTypeObject`.
export function mapPropertyClassIdsToNames(
  values: string | string[] | undefined
): string[] | undefined {
  const list = splitIntoArray(values);
  if (!list.length) return undefined;
  const names = list.map((v) => reversedPropertyTypeObject[v] ?? v).filter(Boolean);
  return names.length ? names : undefined;
}

export const reversedPropTypeCodeObject: Record<string, string> = Object.fromEntries(
  Object.entries(propTypeCodeObject).map(([name, code]) => [code, name])
);

// `prop_type` counterpart of `mapPropertyClassIdsToNames`: normalize HAR.com's
// rental property-type CODES ("SGL,THC") to the same canonical names. HAR sends
// `prop_type` instead of `property_class_id` on for-rent searches, so without
// this the selection is lost and the param leaks into the `extra` passthrough
// bag (surfacing as an inflated "+N HAR.com filters" pill).
//
// Unlike `mapPropertyClassIdsToNames`, unrecognized tokens are DROPPED rather
// than passed through: a `prop_type` value is always a code, so an unknown
// token is malformed input, not a name. Letting it through would fabricate a
// property type that matches no checkbox and no pill label.
export function mapPropTypeCodesToNames(
  values: string | string[] | undefined
): string[] | undefined {
  const list = splitIntoArray(values);
  if (!list.length) return undefined;
  const names = list.map((v) => reversedPropTypeCodeObject[v]).filter(Boolean);
  return names.length ? names : undefined;
}

// Inverse of `mapPropTypeCodesToNames`, for building a for-rent HAR.com query
// string. Names with no rental code are dropped rather than emitted empty.
export function mapNamesToPropTypeCodes(
  values: string | string[] | undefined
): string[] {
  return splitIntoArray(values).map((v) => propTypeCodeObject[v]).filter(Boolean);
}

// export function createFilterOperation<N extends QueryName, F extends QueryMapping[N], O extends string = "search_filter">(search_name: N, type: eQueryType, filters: KnownKeysOnly<F>, operationName?: O): IQueryOperationJson<any, N> {
//   //const operation_name = operationName ?? "search_filter" as O;
//   const operation = new SearchFilterOperation(search_name, filters, type);
//   return operation;
// }

// export function sanitizeSchoolFilters(filters: IFilters): IFilters {
//   const sanitized = { ...filters };
//   SCHOOL_KEYS.forEach((key) => {
//     sanitized[key] = sanitized[key]?.label ?? "";
//   });
//   return sanitized;
// }

// create filter query to use in chat api as payload 
// export const createFilterQuestionAndSelectedFilters = (filters: IFilters, messagesCount = 0) => {
//   const formatKey = (key: string) => key.replace(/([a-z])([A-Z])/g, "$1 $2");
//   const removeIdFromSchoolNameFilters = sanitizeSchoolFilters(filters);
//   // these are the keys (forcl, newconstr, ptids) that can add from ai summary pills filters 
//   const {
//     forcl,
//     newconstr,
//     neighborhood,
//     community,
//     nid,
//     County,
//     ...remainigRemoveIdFromSchoolNameFilters
//   } = removeIdFromSchoolNameFilters as any;
//   const filtersWithCountyLabel = {
//     ...remainigRemoveIdFromSchoolNameFilters,
//     neighborhood: getName(neighborhood),
//     community: getName(community?.[0]),
//     nid: getName(nid),
//     County: getLongValue(countiesLookup, County) || ''
//   };
//   const truthyFiltersArray = Object.entries(filtersWithCountyLabel).filter(([_key, value]) => {
//     // Skip undefined, null, or empty string
//     if (value === undefined || value === null || value === '') return false;

//     // If it's an array, filter out empty/falsy strings and check if any valid items remain
//     if (Array.isArray(value)) {
//       const filteredArray = value.filter(item => {
//         // Ensure item exists and is not just whitespace (for strings), and not null/undefined
//         if (item === '' || item === null || item === undefined) {
//           return false;
//         }
//         if (typeof item === 'string') {
//           return item.trim() !== '';
//         }
//         return true;
//       });
//       return filteredArray.length > 0;
//     }

//     return true;
//   });

//   const truthyFilters = Object.fromEntries(truthyFiltersArray) as Partial<IFilters>;

//   if (truthyFiltersArray.length === 0) return;

//   //const queryPrefix = messagesCount === 0 ? 'Show me properties based on the following filters' : 'Filter Property By';
//   const type = messagesCount === 0 ? eQueryType.Search : eQueryType.Filter;

//   // const tranformFiltersIntoString = truthyFiltersArray
//   //   .flatMap(([key, value]) => `${formatKey(key)}: ${value}`)
//   //   .join(", ") || '';

//   // const attributes = truthyFiltersArray
//   //   .flatMap(([key, value]) => `${key}="${value}"`).join(" ");

//   // Converting IFilters to SearchInputFilters
//   const selectedFilters = toSearchToolInput(truthyFilters);

//   //const question = `${queryPrefix}: ${tranformFiltersIntoString}`;
//   // const tag = 'search-filters';
//   // const question = `<${tag} search_name="search_listings" type="${type}" ${attributes}></${tag}>`;
//   return { selectedFilters, type };
// }

// export const VALUE_SEPARATOR = "$@$";

// export const getName = (value: string = "") => {
//   return typeof value === "string" ? value?.split(VALUE_SEPARATOR)[0] ?? "" : value;
// };

// export const getId = (value: string = "") => {
//   return typeof value === "string" ? value?.split(VALUE_SEPARATOR)[1] ?? "" : value;
// };

// export const setValue = (
//   name: string | number,
//   id: string | number
// ) => {
//   return `${name}${VALUE_SEPARATOR}${id}`;
// };

export const sanitizeString = (string: string = "") => {
  return string
    // NFD (Normalization Form Decomposed) 
    // Decomposes characters into their base character and diacritical marks (e.g., é becomes e + ´).
    .normalize("NFD")                            // normalize unicode (decompose characters)

    // NFC (Normalization Form Composed) 
    // Combines characters that can be combined back into their composed forms (e.g., e + ´ becomes é).
    .normalize("NFC")                            // normalize unicode (compose characters)

    // NFKD (Normalization Form Compatibility Decomposed) 
    // Decomposes compatibility characters into their simpler, non-form characters (e.g., ² becomes 2, © becomes c o p y).
    .normalize("NFKD")                           // normalize unicode (decompose compatibility characters)

    // NFKC (Normalization Form Compatibility Composed) 
    // Combines characters and decomposes compatibility characters, ensuring all are normalized into their simplest form (e.g., ² becomes 2).
    .normalize("NFKC")                           // normalize unicode (compose characters, decompose compatibility)

    // Remove non-ASCII characters (e.g., emojis, non-English letters).
    .replace(/[^\x00-\x7F]/g, "")                 // remove non-ASCII characters (e.g., emojis)

  // Remove specific special characters: &, %, $, #, ?, @, quotes, etc.
  // .replace(/[&%$#?@'"<>]/g, "")                 // remove specific special characters
};

type QuickAccess = string[];

export const replaceQuickAccessItems = (quickAccessArray: QuickAccess): QuickAccess => {
  const replacements: { [key: string]: string } = {
    "forcl": "Foreclosure",
    "new_constr2": "new_constr"
  };

  return quickAccessArray.map(item => {
    // If the item exists in the replacements object, replace it
    if (replacements[item]) {
      return replacements[item];
    }
    // Otherwise, return the item as it is
    return item;
  });
}

export function isIOS() {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') return false;

  // IE Checkup
  if ((window as any).MSStream) { return false; }

  // iOS < 13 or iPhone or iPad with Disabled Desktop mode
  if (/iPad|iPhone|iPod/.test(navigator.platform)) { return true; }

  // iPadOS 13
  if (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1) { return true; }

  // Otherwise, not iOS
  return false;
}

export const getUniqueValues = (array?: string[]) => {
  return [...new Set(array || [])];
};

// Utility function to strip markdown syntax from text
export const stripMarkdown = (text: string): string => {
  if (!text) return text;

  return text
    // Remove images ![alt](url) -> alt
    .replace(/!\[([^\]]*)\]\([^)]+\)/g, '$1')
    // Remove links [text](url) -> text
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
    // Remove code blocks ```text``` -> text
    .replace(/```[\s\S]*?```/g, '')
    // Remove inline code `text` -> text
    .replace(/`([^`]+)`/g, '$1')
    // Remove bold **text** or __text__ -> text
    .replace(/(\*\*|__)(.*?)\1/g, '$2')
    // Remove italic *text* or _text_ -> text
    .replace(/(\*|_)(.*?)\1/g, '$2')
    // Remove strikethrough ~~text~~ -> text
    .replace(/~~(.*?)~~/g, '$1')
    // Remove headings # text -> text
    .replace(/^#{1,6}\s+/gm, '')
    // Remove blockquotes > text -> text
    .replace(/^>\s+/gm, '')
    // Remove horizontal rules --- or ***
    .replace(/^[-*_]{3,}\s*$/gm, '')
    // Remove list markers - item or * item or 1. item -> item
    .replace(/^[\s]*[-*+]\s+/gm, '')
    .replace(/^[\s]*\d+\.\s+/gm, '')
    // Remove HTML tags
    .replace(/<[^>]*>/g, '')
    // Clean up multiple spaces
    .replace(/\s+/g, ' ')
    .trim();
}

export const isNotForSaleStatus = (status: string) => {
  return status === "Not For Sale" || status === "Home Value";
};

export const getPropertyTypeClass = (type: string, status: string) => {
  if (isNotForSaleStatus(status)) return "terminated";
  if (type === "lease") return "forrent_type";
  return "forsale_type";
};

export const getPropertyTypeText = (type: string, status: string) => {
  if (isNotForSaleStatus(status)) return "Not for sale";
  if (type === "lease") return "For Rent";
  return "For Sale";
};

export function typedKeys<T extends object>(obj: T): Array<keyof T> {
  return Object.keys(obj) as Array<keyof T>;
}

export function typedEntries<T extends object>(obj: T): TypedEntries<T> {
  return Object.entries(obj) as TypedEntries<T>;
}

export function detach<T extends object, K extends keyof T, V = T[K]>(input: T, key: K, mapper: (value: T[K]) => V): V;
export function detach<T extends object, K extends keyof T>(input: T, key: K): T[K] | undefined;
export function detach<T extends object, K extends keyof T, V = T[K]>(input: T, key: K, mapper?: (value: T[K]) => V): V | undefined {
  if (!(key in input)) return undefined;
  const value = input[key];
  const mapped = mapper?.(value as T[K]) ?? value as unknown as V;
  delete input[key];
  return mapped;
}

export function bindDetach<T extends object, V, R>(input: T, mapper: (value: V) => R)
  : <K extends KeysOfAnyType<T, V>>(key: K) => R;
export function bindDetach<T extends object>(input: T)
  : <K extends keyof T>(key: K) => T[K]
export function bindDetach<T extends object, V, R>(input: T, mapper: (value: V) => R = x => x as unknown as R)
  : <K extends KeysOfAnyType<T, V>>(key: K) => R {
  return (key: KeysOfAnyType<T, V>) => {
    return detach(input, key, mapper as any);
  }
}


type SwitchArguments<T, N extends number = 8, A extends any[][] = [[T, unknown], [T, unknown, unknown]]> =
  A['length'] extends N ? A[number] : SwitchArguments<T, N, [[...A[0], T, unknown], [...A[0], T, unknown, unknown], ...A]>;


export function switchCase<E, A extends SwitchArguments<E>>(expression: E, ...input: A): IfElseResponse<A>;
export function switchCase<E>(expression: E, ...input: Array<unknown>): unknown {
  if (input.length < 2) { return undefined; }
  while (input.length) {
    if (input.length === 1) { return input.pop(); }
    const caseValue = input.shift();
    const result = input.shift();
    if (expression == caseValue) { return result; }
  }

  return undefined;
}


type IfElseResponse<T extends Array<unknown>, TCondition extends boolean = true> =
  T extends [infer R, ...infer A extends [unknown, ...Array<unknown>]] ?
  (TCondition extends true ? IfElseResponse<A, false> : R | IfElseResponse<A, true>) :
  T extends [infer R] ?
  (TCondition extends true ? R : R | undefined) :
  never;

export function ifElse<T extends SwitchArguments<boolean>>(...input: T): IfElseResponse<T>
export function ifElse(...input: unknown[]): unknown {
  if (input.length < 2) { return undefined; }
  while (input.length) {
    if (input.length === 1) { return input.pop(); }
    const condition = input.shift();
    const result = input.shift();
    if (!!condition) { return result; }
  }

  return undefined;
}